
import { defineComponent, onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Gleap from 'gleap';
import { getGleapUserId, UserState } from '@/composables/User';
import { getShopPlatformVersion, getV4DashboardUrl } from '@/composables/shopVersion';

/**
 * Legacy billing entry points whose new behaviour is to redirect to
 * `/shop-subscription`. When the user lands on `/shop-subscription` *from* one
 * of these paths, hitting "Back" would loop straight back through the redirect.
 * In that case we send them to the platform home instead.
 */
const LEGACY_BILLING_PATHS = ['/shop/plan', '/shop/bills', '/user/profile/offer'];

function pathIsLegacyBilling(path: string | null | undefined): boolean {
  if (!path) return false;
  // Strip query string / hash before comparing.
  const clean = path.split('?')[0].split('#')[0];
  return LEGACY_BILLING_PATHS.includes(clean);
}

function referrerPath(referrer: string): string | null {
  if (!referrer) return null;
  try {
    return new URL(referrer).pathname;
  } catch {
    return null;
  }
}

/**
 * Body class that drives the global CSS rule hiding Gleap's automatic UI
 * (banner / modal / notifications / tours). The Gleap SDK appends those
 * elements directly to `document.body`, outside this component's tree, so
 * scoped CSS can't reach them — and `setDisableInAppNotifications` only
 * covers the `notification` actionType, NOT banners. Hiding via a body
 * class is timing-proof: it works even if Gleap injects the banner after
 * mount (server-side outbound rules).
 */
const GLEAP_SILENT_BODY_CLASS = 'subscription-no-gleap-ui';

export default defineComponent({
  name: 'LayoutSubscription',

  setup() {
    const { t } = useI18n();
    const router = useRouter();

    // Gleap is normally bootstrapped by LayoutMain, but this layout is
    // standalone (the subscription page lives outside the dashboard
    // sidebar). Initialise it here too so that the "Contact us" CTA on
    // the Pro plan card actually opens the chat widget. The Gleap SDK
    // is idempotent — calling initialize twice with the same key is safe.
    //
    // On this page Gleap is kept silent: only the explicit "Contact us"
    // CTA opens it via Gleap.open(). The floating feedback button and any
    // automatic in-app banners (surveys, outbound campaigns, news) are
    // suppressed so the subscription flow is not interrupted. Defaults are
    // restored on unmount to leave LayoutMain unaffected.
    onMounted(() => {
      // Hide any Gleap automatic UI for as long as this layout is mounted.
      // CSS-based, so it covers banners injected *after* mount too — which
      // the JS flags below cannot (banners bypass setDisableInAppNotifications).
      document.body.classList.add(GLEAP_SILENT_BODY_CLASS);

      try {
        Gleap.setLanguage(UserState.user?.lang ?? 'en');
        Gleap.initialize(process.env.VUE_APP_GLEAP_API_KEY || '');
        Gleap.setMaxNetworkRequests(50);
        Gleap.showFeedbackButton(false);
        Gleap.setDisableInAppNotifications(true);
        // Tear down a banner / modal that was already open before we landed
        // here (e.g. opened earlier by LayoutMain during the session).
        Gleap.closeBanner();
        Gleap.closeModal();
        if (UserState.user?.id && !Gleap.isUserIdentified()) {
          Gleap.identify(getGleapUserId(), {
            email: UserState.user.email,
            name: `${UserState.user.firstName ?? ''} ${UserState.user.lastName ?? ''}`.trim(),
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[LayoutSubscription] Gleap init failed', e);
      }
    });

    onBeforeUnmount(() => {
      // Restore Gleap defaults so the rest of the app (LayoutMain) keeps
      // its banners and floating button active.
      document.body.classList.remove(GLEAP_SILENT_BODY_CLASS);
      try {
        Gleap.showFeedbackButton(true);
        Gleap.setDisableInAppNotifications(false);
      } catch {
        // Gleap may not be loaded — fail silently.
      }
    });

    // Captured ONCE at mount: cross-domain provenance (e.g. user clicked a
    // link on my.shopimind.com that opened my.new.shopimind.com/shop-subscription).
    // `document.referrer` is set at page load and never mutates afterwards.
    const initialReferrer = typeof document !== 'undefined' ? document.referrer : '';

    /**
     * Sends the user to the home of the *platform* the shop currently runs on
     * (v4 → external my.shopimind.com, v5 → in-app /dashboard). Used when the
     * normal "back" would loop through a redirected legacy billing path.
     */
    const goToPlatformHome = () => {
      const platform = getShopPlatformVersion();
      if (platform === 'v4') {
        window.location.href = getV4DashboardUrl();
        return;
      }
      router.push({ name: 'home' });
    };

    const onBack = () => {
      // 1. Intra-app provenance (Vue Router pushed us here from another route
      //    of the new app). `history.state.back` is the path of the previous
      //    entry in the browser history stack.
      const intraAppBack = (window.history.state as any)?.back as string | null | undefined;
      if (pathIsLegacyBilling(intraAppBack)) {
        goToPlatformHome();
        return;
      }

      // 2. Cross-domain provenance (user came from the v4 frontend on
      //    my.shopimind.com). Look at the page's referrer captured at mount.
      if (pathIsLegacyBilling(referrerPath(initialReferrer))) {
        goToPlatformHome();
        return;
      }

      // 3. Default: standard back, with a home fallback when the history is
      //    empty (deep link / fresh tab).
      if (window.history.length > 1) {
        router.back();
        return;
      }
      goToPlatformHome();
    };

    return { t, onBack };
  },
});
